'use client'

import { useAuthorization } from '@entities/authentication'
import { tutorConnectEntityQueries, useUnfavoriteTutorMutation } from '@entities/tutor-connect'
import { MAX_BLOCKED_TUTORS, SLOT_FULL_DIALOG, useExclusionListQuery } from '@entities/tutor-exclusion'
import { useBlockTutor, useUnblockTutor } from '@features/tutor-exclusion'
import { track } from '@shared/analytics'
import { useTranslation } from '@shared/config/i18n'
import { EVENT_IDS } from '@shared/constants'
import { openAlertDialogV2 } from '@shared/ui'
import { useQuery } from '@tanstack/react-query'

import { Button } from '@podo-app/design-system-temp'

interface TutorBlockActionProps {
  tutorId: number
  classId?: number
  onActionComplete?: () => void
}

export const TutorBlockAction = ({ tutorId, classId, onActionComplete }: TutorBlockActionProps) => {
  const t = useTranslation()
  const { data } = useExclusionListQuery()
  const { bearerToken } = useAuthorization()
  const { mutate: blockTutor, isPending: isBlocking } = useBlockTutor({ location: 'tutor_profile' })
  const { mutate: unblockTutor, isPending: isUnblocking } = useUnblockTutor({ location: 'tutor_profile' })
  const { data: favorites = [] } = useQuery(tutorConnectEntityQueries.favorites(bearerToken))
  const { mutate: unfavoriteTutor } = useUnfavoriteTutorMutation(bearerToken)

  const isBlocked = data?.blockedTutors?.some((t) => t.tutorId === tutorId) ?? false
  const isFavorited = favorites.some((f) => f.tutorId === tutorId && f.favorite)
  const currentCount = data?.currentCount ?? 0
  const isSlotFull = currentCount >= MAX_BLOCKED_TUTORS

  const executeBlock = () => {
    blockTutor({ tutorId, classId }, { onSuccess: () => onActionComplete?.() })
  }

  // 찜 상태에서 차단 시 "찜 해제하고 차단할까요?" 확인 후 찜 해제 → 차단 (PRD §3.6 / §12.3 상호배타 상태머신)
  const confirmUnfavoriteThenBlock = () => {
    openAlertDialogV2({
      AlertHeader: () => (
        <div className="flex flex-col gap-2 text-center">
          <span className="text-[18px] font-bold leading-[26px] tracking-[-0.36px] text-[#1C1C1C]">
            {t('tutorExclusion.action.unfavoriteAndBlockTitle')}
          </span>
          <span className="whitespace-pre-line text-[16px] font-medium leading-6 tracking-[-0.32px] text-[#757575]">
            {t('tutorExclusion.action.unfavoriteAndBlockDescription')}
          </span>
        </div>
      ),
      AlertFooter: ({ handleClose }) => (
        <>
          <Button variant="ghost" size="xLarge" fullWidth onClick={handleClose}>
            {t('common.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            size="xLarge"
            fullWidth
            onClick={() => {
              handleClose()
              unfavoriteTutor(tutorId, { onSettled: () => executeBlock() })
            }}
          >
            {t('tutorExclusion.action.block')}
          </Button>
        </>
      ),
    })
  }

  const handleBlock = () => {
    track(EVENT_IDS.TUTOR_EXCLUSION_ACTION_CLICKED, {
      tutorId,
      classId,
      location: 'tutor_profile',
      action: 'block',
      state: isSlotFull ? 'slot_full' : isFavorited ? 'favorited' : 'ready',
    })
    if (isSlotFull) {
      openAlertDialogV2({
        AlertHeader: () => (
          <div className="flex flex-col gap-2 text-center">
            <span className="text-[18px] font-bold leading-[26px] tracking-[-0.36px] text-[#1C1C1C]">
              {t('tutorExclusion.slotFullDialog.title')}
            </span>
            <span className="whitespace-pre-line text-[16px] font-medium leading-6 tracking-[-0.32px] text-[#757575]">
              {t('tutorExclusion.slotFullDialog.description', { count: MAX_BLOCKED_TUTORS })}
            </span>
          </div>
        ),
        AlertFooter: ({ handleClose, push }) => (
          <Button
            variant="primary"
            size="xLarge"
            fullWidth
            onClick={() => {
              handleClose()
              push(SLOT_FULL_DIALOG.REDIRECT_PATH)
            }}
          >
            {t('tutorExclusion.slotFullDialog.confirm')}
          </Button>
        ),
      })
      return
    }

    if (isFavorited) {
      confirmUnfavoriteThenBlock()
      return
    }

    executeBlock()
  }

  const handleUnblock = () => {
    track(EVENT_IDS.TUTOR_EXCLUSION_ACTION_CLICKED, {
      tutorId,
      classId,
      location: 'tutor_profile',
      action: 'unblock',
      state: 'already_blocked',
    })
    unblockTutor(tutorId, { onSuccess: () => onActionComplete?.() })
  }

  if (isBlocked) {
    return (
      <div className="flex items-center justify-between rounded-xl bg-[#F5F5F5] px-4 py-3">
        <span className="text-[14px] font-medium leading-5 text-[#1C1C1C]">
          {t('tutorExclusion.action.blockedState')}
        </span>
        <button
          onClick={handleUnblock}
          disabled={isUnblocking}
          className="rounded-lg border-[2px] border-[#1C1C1C] bg-[#6184FF] px-3 py-[3px] text-[12px] font-bold text-white shadow-[0_4px_0_0_#1C1C1C] transition-all hover:translate-y-1 hover:shadow-none disabled:opacity-50"
          data-testid={`btn-unblock-tutor-${tutorId}`}
        >
          {t('tutorExclusion.action.unblock')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between rounded-xl bg-[#F5F5F5] px-4 py-3">
      <span className="text-[14px] font-medium leading-5 text-[#1C1C1C]">
        {t('tutorExclusion.action.blockQuestion')}
      </span>
      <button
        onClick={handleBlock}
        disabled={isBlocking}
        className="rounded-lg border-[2px] border-[#1C1C1C] bg-[#FC6671] px-3 py-[3px] text-[12px] font-bold text-white shadow-[0_4px_0_0_#1C1C1C] transition-all hover:translate-y-1 hover:shadow-none disabled:opacity-50"
        data-testid={`btn-block-tutor-${tutorId}`}
      >
        {t('tutorExclusion.action.block')}
      </button>
    </div>
  )
}
